import React, { useState, useEffect, useCallback } from "react";
import "./NotificationPanel.css";
import { Bell, ChevronRight, FolderOpen } from "lucide-react";
import axios from "axios";
import { Link } from "react-router-dom";

const DRAFTS_KEY = "elitecore-mail-drafts";
const SENT_KEY = "elitecore-mail-sent";

const normalizeNotificationType = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "crm") return "CRM";
  if (raw === "production") return "Production";
  if (raw === "mention") return "Mention";
  if (raw === "order") return "Order";
  if (raw === "feed") return "Feed";
  if (raw === "chat") return "Chat";
  if (raw === "project note") return "Project note";
  if (raw === "system" || raw === "general") return "System";
  return "";
};

const notificationTypeLabel = (note = {}) => {
  const explicit = normalizeNotificationType(note.notificationCategory || note.typeLabel);
  if (explicit) return explicit;
  if (note.crm) return "CRM";
  if (note.production) return "Production";
  if (note.feed) return "Feed";
  if (note.mention) return "Mention";
  if (note.OrderId || note.orderId) return note.chatSummary ? "Chat" : "Order";
  if (note.chatMessageId || note.chatSummary) return "Chat";
  return "System";
};

const normalizeSeverity = (severity) => {
  const raw = String(severity || "").trim().toLowerCase();
  if (raw === "red" || raw === "danger" || raw === "critical" || raw === "error") return "danger";
  if (raw === "yellow" || raw === "warning" || raw === "warn") return "warning";
  if (raw === "info" || raw === "notice") return "info";
  return "";
};

const NotificationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [allNotifications, setAllNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showBadge, setShowBadge] = useState(true);
  const [clearingNotifications, setClearingNotifications] = useState(false);

  const [panelMode, setPanelMode] = useState("live");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [presenceGhost, setPresenceGhost] = useState(false);
  const [presenceLoading, setPresenceLoading] = useState(false);
  const [presenceLoadedAt, setPresenceLoadedAt] = useState(0);
  const [mailFolder, setMailFolder] = useState("inbox");
  const [drafts, setDrafts] = useState([]);
  const [sent, setSent] = useState([]);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "" });

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const createdAt = new Date(timestamp);
    const secondsAgo = Math.floor((now - createdAt) / 1000);

    if (secondsAgo < 1) return "Just now";
    if (secondsAgo < 60) return `${secondsAgo} second${secondsAgo !== 1 ? "s" : ""} ago`;

    const minutesAgo = Math.floor(secondsAgo / 60);
    if (minutesAgo < 60) return `${minutesAgo} minute${minutesAgo !== 1 ? "s" : ""} ago`;

    const hoursAgo = Math.floor(minutesAgo / 60);
    if (hoursAgo < 24) return `${hoursAgo} hour${hoursAgo !== 1 ? "s" : ""} ago`;

    const daysAgo = Math.floor(hoursAgo / 24);
    if (daysAgo <= 7) return `${daysAgo} day${daysAgo !== 1 ? "s" : ""} ago`;

    return createdAt.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const loadLocalMailData = () => {
    try {
      const draftRows = JSON.parse(localStorage.getItem(DRAFTS_KEY) || "[]");
      const sentRows = JSON.parse(localStorage.getItem(SENT_KEY) || "[]");
      setDrafts(Array.isArray(draftRows) ? draftRows : []);
      setSent(Array.isArray(sentRows) ? sentRows : []);
    } catch {
      setDrafts([]);
      setSent([]);
    }
  };

  const getInstanceUniqueId = useCallback(() => {
    const match = window.location.pathname.match(/\/instance\/([^/]+)/i);
    return match?.[1] ? decodeURIComponent(match[1]) : "";
  }, []);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("accessToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const fetchOnlineUsers = useCallback(async () => {
    const instanceUniqueId = getInstanceUniqueId();
    if (!instanceUniqueId) return;

    try {
      setPresenceLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/presence/online`,
        {
          ...authHeaders(),
          params: { instanceUniqueId },
        }
      );
      setOnlineUsers(Array.isArray(response.data?.online) ? response.data.online : []);
      setPresenceLoadedAt(Date.now());
      setPresenceGhost(Boolean(response.data?.isGhost));
    } catch (err) {
      console.error("Error fetching online users:", err);
    } finally {
      setPresenceLoading(false);
    }
  }, [authHeaders, getInstanceUniqueId]);

  const sendPresenceHeartbeat = useCallback(async () => {
    const instanceUniqueId = getInstanceUniqueId();
    if (!instanceUniqueId || document.visibilityState !== "visible") return;

    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/presence/heartbeat`,
        { instanceUniqueId },
        authHeaders()
      );
    } catch (err) {
      console.error("Error updating presence:", err);
    }
  }, [authHeaders, getInstanceUniqueId]);

  const toggleGhostMode = async () => {
    const instanceUniqueId = getInstanceUniqueId();
    if (!instanceUniqueId) return;

    const nextGhost = !presenceGhost;
    try {
      setPresenceGhost(nextGhost);
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/presence/ghost`,
        { instanceUniqueId, isGhost: nextGhost },
        authHeaders()
      );
      fetchOnlineUsers();
    } catch (err) {
      setPresenceGhost(!nextGhost);
      console.error("Error updating ghost mode:", err);
    }
  };

  const resolveProfilePicture = (value) => {
    const getPath = (input) => {
      if (!input) return "";
      if (Array.isArray(input)) {
        return getPath(input.find((entry) => entry?.selected === true) || input[0]);
      }
      if (typeof input === "object") return getPath(input.url || input.path || input.src);
      if (typeof input !== "string") return "";
      const trimmed = input.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("[") || trimmed.startsWith("{") || trimmed.startsWith('"')) {
        try { return getPath(JSON.parse(trimmed)); } catch { return ""; }
      }
      return trimmed;
    };
    const path = getPath(value);
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    if (/^(data:|javascript:|\/\/)/i.test(path)) return "";
    const base = String(process.env.REACT_APP_NODEJS_SERVER_URL || "").replace(/\/$/, "");
    return `${base}${path.startsWith("/") ? path : `/${path}`}`;
  };

  const initialsForName = (name = "") =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "U";

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const headers = { headers: { Authorization: `Bearer ${token}` } };

      const [unreadRes, allRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/unread/`, headers),
        axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/all/`, headers),
      ]);

      const { chatSummary = [], mentionSummary = [], mentionDetails = [], crmDetails = [], productionDetails = [], feedNotifications = [] } = unreadRes.data || {};
      const mergedNotifications = [];

      feedNotifications.forEach((feed) => {
        mergedNotifications.push({
          id: `feed-${feed.id}`,
          feed: true,
          typeLabel: "Feed",
          title: feed.title,
          message: feed.title || "New feed update",
          createdAt: feed.createdAt,
          seen: false,
        });
      });

      mentionDetails.forEach((mention) => {
        const isWebStore = mention.webStoreId !== null;
        const isCustomer = mention.customerId !== null;

        mergedNotifications.push({
          id: `mention-${mention.id}`,
          mention: true,
          typeLabel: mention.activityType === 'note' ? 'Project note' : 'Mention',
          isWebStore,
          isCustomer,
          orderId: mention.orderId,
          webStoreId: mention.webStoreId,
          customerId: mention.customerId,
          instanceId: mention.instanceId,
          message: mention.message,
          createdAt: mention.createdAt,
          seen: false,
          by: mention.by,
          sender: mention.sender || null,
        });
      });

      chatSummary.forEach((chat) => {
        mergedNotifications.push({
          id: `chat-${chat.orderId}`,
          mention: false,
          chatSummary: true,
          typeLabel: chat.activityType === 'note' ? 'Project note' : 'Chat',
          activityType: chat.activityType,
          orderId: chat.orderId,
          count: chat.count,
          createdAt: chat.createdAt,
          seen: false,
          sender: chat.sender || null,
          message: chat.latestMessage || '',
        });
      });

      crmDetails.forEach((crm) => {
        mergedNotifications.push({
          id: `crm-${crm.id}`,
          notificationId: crm.id,
          crm: true,
          typeLabel: "CRM",
          message: crm.message,
          notificationType: crm.notificationType,
          severity: crm.severity,
          actionUrl: crm.actionUrl,
          metadata: crm.metadata || {},
          createdAt: crm.createdAt,
          seen: false,
        });
      });
      productionDetails.forEach((production) => {
        mergedNotifications.push({
          id: `production-${production.id}`, notificationId: production.id, production: true,
          typeLabel: "Production", message: production.message, notificationType: production.notificationType,
          severity: production.severity, actionUrl: production.actionUrl, metadata: production.metadata || {},
          createdAt: production.createdAt, seen: false,
        });
      });

      setNotifications(mergedNotifications);

      const totalChatCount = chatSummary.reduce((sum, chat) => sum + chat.count, 0);
      const totalMentionCount = mentionSummary.reduce((sum, mention) => sum + mention.count, 0);
      const total = totalChatCount + totalMentionCount + crmDetails.length + productionDetails.length + feedNotifications.length;
      setUnreadCount(total);
      setShowBadge(total > 0);

      const fullList = Array.isArray(allRes.data) ? allRes.data : [];
      setAllNotifications(fullList);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    loadLocalMailData();
    sendPresenceHeartbeat();

    const notificationInterval = setInterval(() => {
      fetchNotifications();
      loadLocalMailData();
    }, 10000);

    const heartbeatInterval = setInterval(sendPresenceHeartbeat, 5 * 60 * 1000);
    const handleActivity = () => {
      if (document.visibilityState === "visible") sendPresenceHeartbeat();
    };
    window.addEventListener("focus", handleActivity);
    document.addEventListener("visibilitychange", handleActivity);

    return () => {
      clearInterval(notificationInterval);
      clearInterval(heartbeatInterval);
      window.removeEventListener("focus", handleActivity);
      document.removeEventListener("visibilitychange", handleActivity);
    };
  }, [sendPresenceHeartbeat]);

  const persistDrafts = (rows) => {
    setDrafts(rows);
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(rows));
  };

  const persistSent = (rows) => {
    setSent(rows);
    localStorage.setItem(SENT_KEY, JSON.stringify(rows));
  };

  const handleSaveDraft = () => {
    if (!compose.subject && !compose.body && !compose.to) return;
    const row = { id: `d-${Date.now()}`, ...compose, createdAt: new Date().toISOString() };
    persistDrafts([row, ...drafts].slice(0, 100));
    setCompose({ to: "", subject: "", body: "" });
  };

  const handleSend = () => {
    if (!compose.to || !compose.subject || !compose.body) return;
    const row = { id: `s-${Date.now()}`, ...compose, createdAt: new Date().toISOString() };
    persistSent([row, ...sent].slice(0, 100));
    setCompose({ to: "", subject: "", body: "" });
    setMailFolder("sent");
  };

  const handleNotificationClick = async (orderId, webStoreId, customerId, instanceId, notificationId) => {
    try {
      const token = localStorage.getItem("accessToken");
      let requestBody = {};

      if (webStoreId) {
        requestBody = { contextType: "webStore", contextId: webStoreId };
      } else if (customerId) {
        requestBody = { contextType: "customer", contextId: customerId };
      } else if (instanceId) {
        requestBody = { contextType: "instance", contextId: instanceId };
      } else if (orderId) {
        requestBody = { orderId };
      }

      await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/mark-order-read`, requestBody, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) => (notif.id === notificationId ? { ...notif, seen: true } : notif))
      );
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const handleClearNotifications = async (event) => {
    event?.stopPropagation();

    if (clearingNotifications || unreadCount === 0) return;

    try {
      setClearingNotifications(true);
      const token = localStorage.getItem("accessToken");

      await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/clear-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications([]);
      setUnreadCount(0);
      setShowBadge(false);
      fetchNotifications();
    } catch (err) {
      console.error("Error clearing notifications:", err);
    } finally {
      setClearingNotifications(false);
    }
  };

  const handleCrmNotificationClick = async (notificationId) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.post(`${process.env.REACT_APP_NODEJS_SERVER_URL}/notifications/mark-as-read`, {
        notificationIds: [notificationId],
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) => (notif.notificationId === notificationId ? { ...notif, seen: true } : notif))
      );
      fetchNotifications();
    } catch (err) {
      console.error("Error marking CRM notification as read:", err);
    }
  };

  const inboxRows = allNotifications.map((note) => ({
    id: note.id,
    typeLabel: notificationTypeLabel(note),
    severity: normalizeSeverity(note.severity),
    subject: note.notificationCategory === "crm" || note.notificationCategory === "production"
      ? `CRM Alert: ${(note.notificationType || "notification").replaceAll("_", " ")}`
      : note.mention ? `Mention in Project #${note.OrderId || "—"}` : `Notification for Project #${note.OrderId || "—"}`,
    preview: note.message || "",
    createdAt: note.createdAt,
  }));

  const folderRows = mailFolder === "inbox" ? inboxRows : mailFolder === "drafts" ? drafts : sent;

  useEffect(() => {
    if (!isOpen) return undefined;
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 60000);
    if (panelMode === "online") sendPresenceHeartbeat().finally(fetchOnlineUsers);
    return () => clearInterval(interval);
  }, [fetchOnlineUsers, isOpen, panelMode, sendPresenceHeartbeat]);

  const onlineIds = new Set(presenceLoadedAt && Date.now() - presenceLoadedAt < 2 * 60000 ? onlineUsers.map((user) => Number(user.id)) : []);
  const notificationDestination = (note) => {
    const instance = encodeURIComponent(getInstanceUniqueId() || 'elite-promo-marketing');
    if (note.isCustomer) return `/instance/${instance}/sales/customers?openCustomer=${note.customerId}`;
    if (note.isWebStore) return `/instance/${instance}/sales/promostores/${note.webStoreId}`;
    return `/instance/${instance}/sales/orders/${note.orderId}/${note.chatSummary && note.activityType === 'chat' ? 'chat' : 'overview'}`;
  };

  return (
    <div className="notification-wrapper">
      <div className="notification-icon" onClick={() => setIsOpen((prev) => !prev)}>
        <Bell size={24} color="#333" />
        {showBadge && unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </div>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-rich-heading"><h4 className="notification-title">Latest Updates</h4><button type="button" className="notification-read-all" onClick={handleClearNotifications} disabled={clearingNotifications || unreadCount === 0}>Mark all as read</button></div>

          <div className="notification-mode-row">
            <button className={`mode-btn ${panelMode === "live" ? "active" : ""}`} onClick={() => setPanelMode("live")}>Live</button>
            <button className={`mode-btn ${panelMode === "mail" ? "active" : ""}`} onClick={() => setPanelMode("mail")}>Mail Mode</button>
            <button className={`mode-btn ${panelMode === "online" ? "active" : ""}`} onClick={() => setPanelMode("online")}>Who's Online</button>
            {panelMode === "live" ? (
              <button
                className="mode-btn clear-btn"
                onClick={handleClearNotifications}
                disabled={clearingNotifications || unreadCount === 0}
                title="Clear all live notifications"
              >
                {clearingNotifications ? "Clearing..." : "Clear"}
              </button>
            ) : null}
          </div>

          {panelMode === "live" ? (
            <div className="notification-list">
              {notifications.length > 0 ? (
                notifications.map((note) => {
                  const sender = note.sender;
                  const avatar = sender ? resolveProfilePicture(sender.profilePicture) : '';
                  const online = sender && onlineIds.has(Number(sender.id));
                  const destination = note.crm || note.production ? note.actionUrl : note.feed ? null : notificationDestination(note);
                  const content = (
                    <div className="notification-rich-content">
                      <div className="notification-rich-avatar">
                        {avatar ? <img src={avatar} alt="" /> : <span>{initialsForName(sender?.name || notificationTypeLabel(note))}</span>}
                        {online ? <span className="notification-online-dot" title="Online" aria-label="Online" /> : null}
                      </div>
                      <div className="notification-rich-body">
                        <div className="notification-rich-top">
                          <span className={`notification-type-chip ${notificationTypeLabel(note).toLowerCase()}`}>{notificationTypeLabel(note)}</span>
                          {normalizeSeverity(note.severity) ? <span className={`notification-severity-chip ${normalizeSeverity(note.severity)}`}>{normalizeSeverity(note.severity)}</span> : null}
                          <span className="notification-rich-time">{note.createdAt ? getTimeAgo(note.createdAt) : ''}</span>
                          <span className="notification-unread-dot" aria-label="Unread" />
                        </div>
                        <div className="notification-rich-name">{sender?.name || (note.mention ? note.by : note.production ? 'Production' : note.crm ? 'CRM' : note.feed ? 'Company feed' : 'Project update')}</div>
                        <div className="notification-rich-preview">
                          {/^SYS:/i.test(String(note.message || '')) ? 'Project activity updated' : note.message || (note.chatSummary ? `${note.count} new message${note.count === 1 ? '' : 's'} in this project` : 'New notification')}
                        </div>
                        {note.count > 1 ? <div className="notification-rich-count">{note.count} unread messages</div> : null}
                        {note.orderId ? <div className="notification-project-chip"><FolderOpen size={13} /> Project #{note.orderId}</div> : null}
                      </div>
                      {destination ? <ChevronRight size={16} className="notification-rich-arrow" /> : null}
                    </div>
                  );
                  return <div className="notification-item notification-rich-item" key={note.id}>
                    {destination ? <Link to={destination} className="notification-rich-link" onClick={() => {
                      if (note.crm || note.production) handleCrmNotificationClick(note.notificationId);
                      else handleNotificationClick(note.orderId, note.webStoreId, note.customerId, note.instanceId, note.id);
                      setIsOpen(false);
                    }}>{content}</Link> : content}
                  </div>;
                })
              ) : (
                <div className="notification-item no-notifications">No new notifications</div>
              )}
            </div>
          ) : panelMode === "mail" ? (
            <div className="mail-mode-wrap">
              <div className="mail-folder-tabs">
                {[
                  { key: "inbox", label: `Inbox (${inboxRows.length})` },
                  { key: "drafts", label: `Drafts (${drafts.length})` },
                  { key: "sent", label: `Sent (${sent.length})` },
                ].map((tab) => (
                  <button key={tab.key} className={`mail-tab-btn ${mailFolder === tab.key ? "active" : ""}`} onClick={() => setMailFolder(tab.key)}>
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="notification-list">
                {folderRows.length ? (
                  folderRows.map((row) => (
                    <div className="notification-item" key={row.id}>
                      <div className="notification-meta-row">
                        <span className={`notification-type-chip ${notificationTypeLabel(row).toLowerCase()}`}>
                          {notificationTypeLabel(row)}
                        </span>
                        {normalizeSeverity(row.severity) ? (
                          <span className={`notification-severity-chip ${normalizeSeverity(row.severity)}`}>
                            {normalizeSeverity(row.severity)}
                          </span>
                        ) : null}
                      </div>
                      <div className="notification-message"><strong>{row.subject || "(no subject)"}</strong></div>
                      <div className="notification-time">{row.createdAt ? getTimeAgo(row.createdAt) : ""}</div>
                      {row.preview || row.body ? <div className="mail-preview">{(row.preview || row.body || "").slice(0, 120)}</div> : null}
                    </div>
                  ))
                ) : (
                  <div className="notification-item no-notifications">No items in {mailFolder}.</div>
                )}
              </div>

              <div className="mail-compose">
                <input
                  className="mail-input"
                  placeholder="To"
                  value={compose.to}
                  onChange={(e) => setCompose((p) => ({ ...p, to: e.target.value }))}
                />
                <input
                  className="mail-input"
                  placeholder="Subject"
                  value={compose.subject}
                  onChange={(e) => setCompose((p) => ({ ...p, subject: e.target.value }))}
                />
                <textarea
                  className="mail-textarea"
                  placeholder="Write a message..."
                  value={compose.body}
                  onChange={(e) => setCompose((p) => ({ ...p, body: e.target.value }))}
                />
                <div className="mail-actions">
                  <button className="mail-btn draft" onClick={handleSaveDraft}>Save Draft</button>
                  <button className="mail-btn send" onClick={handleSend}>Send</button>
                </div>
              </div>
            </div>
          ) : panelMode === "online" ? (
            // Presence is intentionally isolated from live messages and mail.
            <div className="presence-wrap">
              <div className="presence-summary">
                <span>{onlineUsers.length} online</span>
                <button className="presence-refresh-btn" onClick={fetchOnlineUsers} disabled={presenceLoading}>
                  {presenceLoading ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              <div className="presence-list">
                {presenceLoading && onlineUsers.length === 0 ? (
                  <div className="presence-empty">Checking who's online...</div>
                ) : onlineUsers.length ? (
                  onlineUsers.map((user) => {
                    const avatarUrl = resolveProfilePicture(user.profilePicture);
                    return (
                      <div className="presence-user" key={user.id}>
                        <div className="presence-avatar-wrap">
                          {avatarUrl ? (
                            <img className="presence-avatar" src={avatarUrl} alt="" />
                          ) : (
                            <div className="presence-avatar presence-avatar-fallback">{initialsForName(user.name)}</div>
                          )}
                          <span className="presence-dot" />
                        </div>
                        <div className="presence-name">{user.name}</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="presence-empty">No one else is showing as online right now.</div>
                )}
              </div>

              <div className="presence-footer">
                <div>
                  <div className="presence-footer-title">Appear offline</div>
                  <div className="presence-footer-copy">Hide yourself from Who's Online.</div>
                </div>
                <button
                  type="button"
                  className={`presence-toggle ${presenceGhost ? "active" : ""}`}
                  onClick={toggleGhostMode}
                  aria-pressed={presenceGhost}
                  aria-label="Appear offline"
                >
                  <span />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;
